import React, { useState, useEffect } from 'react';
import { Box, IconButton, Fab, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { Phone, Close } from '@mui/icons-material';
import api from '../../services/api';
import { usePharmacyStore } from '../../stores/pharmacyStore';

export const RINGCENTRAL_PANEL_WIDTH = 320;

interface RingCentralWidgetProps {
  defaultOpen?: boolean;
  position?: 'left' | 'right';
  onOpenChange?: (open: boolean) => void;
}

const RingCentralWidget: React.FC<RingCentralWidgetProps> = ({
  defaultOpen = false,
  position = 'right',
  onOpenChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isLoading, setIsLoading] = useState(true);
  const [jwtConfig, setJwtConfig] = useState<{
    clientId: string;
    clientSecret: string;
    jwt: string;
  } | null>(null);

  const selectedPharmacyId = usePharmacyStore((state) => state.selectedPharmacyId);
  const isEnabled = import.meta.env.VITE_RINGCENTRAL_ENABLED === 'true';

  useEffect(() => {
    // Skip if RingCentral is disabled
    if (!isEnabled) return;
    // Fetch JWT configuration from backend when pharmacy changes or on mount
    const fetchJwtConfig = async () => {
      if (!selectedPharmacyId) {
        console.log('No pharmacy selected yet');
        setIsLoading(false);
        return;
      }
      
      console.log('Fetching JWT config for pharmacy:', selectedPharmacyId);
      setIsLoading(true);
      
      try {
        const response = await api.get(`/api/pharmacy-configuration/${selectedPharmacyId}/jwt-config`);
        if (response.data && response.data.clientId && response.data.clientSecret && response.data.jwt) {
          console.log('JWT config loaded successfully');
          setJwtConfig(response.data);
        } else {
          console.log('No JWT config in response');
          setJwtConfig(null);
        }
      } catch (error: any) {
        console.log('JWT config not available for pharmacy:', selectedPharmacyId, error.response?.status);
        setJwtConfig(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJwtConfig();
  }, [selectedPharmacyId, isEnabled]);

  const toggleWidget = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onOpenChange?.(newState);
    
    // Save preference to localStorage for desktop only
    if (!isMobile) {
      localStorage.setItem('ringCentralOpen', JSON.stringify(newState));
    }
  };

  // Load saved preference on mount for desktop
  useEffect(() => {
    if (!isEnabled) return;
    if (!isMobile) {
      const savedOpen = localStorage.getItem('ringCentralOpen');
      if (savedOpen !== null) {
        const open = JSON.parse(savedOpen);
        setIsOpen(open);
        onOpenChange?.(open);
      }
    }
  }, [isMobile, onOpenChange, isEnabled]);

  const getIframeSrc = () => {
    // Use JWT from database if available
    if (jwtConfig && jwtConfig.jwt) {
      console.log('JWT Config available:', {
        hasClientId: !!jwtConfig.clientId,
        hasClientSecret: !!jwtConfig.clientSecret,
        hasJwt: !!jwtConfig.jwt,
        jwtPreview: jwtConfig.jwt.substring(0, 50) + '...'
      });
      
      const params = new URLSearchParams({
        clientId: jwtConfig.clientId,
        clientSecret: jwtConfig.clientSecret,
        jwt: jwtConfig.jwt,
        enableSideWidget: '1', // Enable new 3.x side widget features
        disableGlip: '0', // Enable team messaging  
        enableWebRTCPlanB: '0', // Use modern WebRTC
        disconnectInactiveWebphone: '1', // Disconnect inactive connections
        enableRingtoneSettings: '1', // Allow ringtone customization
      });
      
      console.log('Using JWT authentication from database');
      console.log('Full URL:', `https://apps.ringcentral.com/integration/ringcentral-embeddable/3.x/app.html?${params.toString().substring(0, 200)}...`);
      return `https://apps.ringcentral.com/integration/ringcentral-embeddable/3.x/app.html?${params.toString()}`;
    } else {
      // Fallback to OAuth flow if no JWT in database
      console.log('No JWT config, falling back to OAuth');
      const clientId = import.meta.env.VITE_RINGCENTRAL_CLIENT_ID || 'ZysjNrX6b4DdqkV67FxDET';
      const params = new URLSearchParams({
        clientId: clientId,
        enableSideWidget: '1',
        disableGlip: '0',
        enableWebRTCPlanB: '0',
        disconnectInactiveWebphone: '1',
        enableRingtoneSettings: '1',
      });
      
      console.log('Using OAuth flow for RingCentral with clientId:', clientId);
      return `https://apps.ringcentral.com/integration/ringcentral-embeddable/3.x/app.html?${params.toString()}`;
    }
  };

  // Render content for the panel
  const renderPanelContent = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper' }}>
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 1,
          py: 1,
          borderBottom: '1px solid #e0e0e0',
          minHeight: 48
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Phone sx={{ color: '#FF8800' }} />
          <span style={{ fontWeight: 500 }}>RingCentral Phone</span>
        </Box>
        <IconButton onClick={toggleWidget} size="small">
          <Close />
        </IconButton>
      </Box>

      {/* RingCentral iframe */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {isLoading ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            flexDirection: 'column',
            gap: 2
          }}>
            <span>Loading RingCentral...</span>
            <span style={{ fontSize: '12px', color: '#666' }}>
              {selectedPharmacyId ? `Pharmacy: ${selectedPharmacyId}` : 'No pharmacy selected'}
            </span>
          </Box>
        ) : (
          <iframe
            key={`${selectedPharmacyId}-${jwtConfig ? 'jwt' : 'oauth'}`}
            id="rc-widget"
            width="100%"
            height="100%"
            allow="autoplay; microphone; camera"
            src={getIframeSrc()}
            style={{ border: 'none' }}
            title="RingCentral Phone Widget"
          />
        )}
      </Box>
    </Box>
  );

  // Early return if RingCentral is disabled - after all hooks
  if (!isEnabled) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button - Hide when panel is open on desktop */}
      {(!isOpen || isMobile) && (
        <Tooltip title="Open RingCentral Phone">
          <Fab
            color="primary"
            aria-label="ringcentral-phone"
            onClick={toggleWidget}
            sx={{
              position: 'fixed',
              bottom: 16,
              [position]: 16,
              zIndex: 1300,
              backgroundColor: '#FF8800',
              '&:hover': {
                backgroundColor: '#E67700'
              },
              // Smooth fade in animation
              animation: 'fadeIn 0.3s ease-in',
              '@keyframes fadeIn': {
                from: {
                  opacity: 0,
                  transform: 'scale(0.8)',
                },
                to: {
                  opacity: 1,
                  transform: 'scale(1)',
                },
              },
            }}
          >
            <Phone />
          </Fab>
        </Tooltip>
      )}

      {/* Mobile: Overlay Panel */}
      {isMobile && (
        <>
          {/* Backdrop for mobile */}
          {isOpen && (
            <Box
              onClick={toggleWidget}
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: theme.zIndex.drawer - 1,
              }}
            />
          )}
          
          {/* Mobile Panel */}
          <Box
            sx={{
              position: 'fixed',
              [position]: isOpen ? 0 : -RINGCENTRAL_PANEL_WIDTH,
              top: 0,
              width: RINGCENTRAL_PANEL_WIDTH,
              height: '100vh',
              zIndex: theme.zIndex.drawer,
              boxShadow: isOpen ? theme.shadows[16] : 'none',
              transition: theme.transitions.create([position], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            }}
          >
            {renderPanelContent()}
          </Box>
        </>
      )}

      {/* Desktop: Fixed Side Panel */}
      {!isMobile && (
        <Box
          sx={{
            position: 'fixed',
            [position]: isOpen ? 0 : -RINGCENTRAL_PANEL_WIDTH,
            top: 64, // Below the topbar
            width: RINGCENTRAL_PANEL_WIDTH,
            height: 'calc(100vh - 64px)',
            zIndex: theme.zIndex.drawer - 10, // Below drawer but above content
            boxShadow: isOpen ? theme.shadows[8] : 'none',
            borderLeft: position === 'right' ? '1px solid #e0e0e0' : 'none',
            borderRight: position === 'left' ? '1px solid #e0e0e0' : 'none',
            transition: theme.transitions.create([position], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {renderPanelContent()}
        </Box>
      )}
    </>
  );
};

export default RingCentralWidget;